import { OnboardingModule } from "components/common/OnboardingModule";
import { OnboardingTask } from "components/common/OnboardingTypes";
import { useUpdateProductPrompts } from "services/nux";
import { useContext, useEffect, useState } from "react";
import { PROD_ONBOARDING_AVAILABILITY_FORM_UUID } from "components/documents/constants";
import { Collapse } from "@mui/material";
import { Member } from "protogen/common_pb";
import { useNavigate } from "react-router-dom";
import { Family } from "protogen/advisors_service_pb";
import { memberOnboardingWelcomeSubtitle } from "common/memberModuleResolver";
import { CurrentUserContext } from "components/context/RequireAuth";
import { planFlagsFromStatus } from "utils/memberFamilyPlan";
import useDigitalSubscriptionUX from "components/hooks/useDigitalSubscriptionUX";

interface MemberOnboardingModuleProps {
  member: Member;
  family: Family;
  showGoogleCalendarSyncUpsell?: boolean;
  additionalTasks?: OnboardingTask[];
}

export default ({
  member,
  family,
  showGoogleCalendarSyncUpsell,
  additionalTasks = [],
}: MemberOnboardingModuleProps) => {
  const navigate = useNavigate();
  const currentUser = useContext(CurrentUserContext);
  const { isUnpaidSignup, familyStatus } = planFlagsFromStatus(
    currentUser.familyStatus,
  );
  const isDigitalSubscription = useDigitalSubscriptionUX();
  const { request: updateProductPrompts } = useUpdateProductPrompts();
  const [productPrompts, setProductPrompts] = useState<any>(null);

  useEffect(() => {
    if (member) {
      try {
        const productPrompts = JSON.parse(member?.productPrompts || "{}");
        setProductPrompts(productPrompts);
      } catch (e) {}
    }
  }, [member]);

  const handleDismissTask = async (taskKey: string) => {
    await updateProductPrompts({ key: taskKey, value: "dismissed" });
    setProductPrompts((prev: any) => ({
      ...prev,
      [taskKey]: "dismissed",
    }));
  };

  const handleAddFirstTask = () => {
    navigate("/tasks/add", { state: { family: family } });
  };

  const standardTasks: OnboardingTask[] = [
    {
      label: "Add your first task",
      completed: productPrompts?.hasCreatedFirstTask,
      url: "/tasks/add?from=onboarding",
      dismissible: true,
      dismissed: productPrompts?.hasCreatedFirstTask === "dismissed",
      onDismiss: () => handleDismissTask("hasCreatedFirstTask"),
      onClick: handleAddFirstTask,
    },
    ...(!showGoogleCalendarSyncUpsell
      ? [
          {
            label: "Set up your calendar integration",
            completed: productPrompts?.hasCalendarIntegration,
            url: "/settings/calendars?from=onboarding",
            dismissible: true,
            dismissed: productPrompts?.hasCalendarIntegration === "dismissed",
            onDismiss: () => handleDismissTask("hasCalendarIntegration"),
          },
        ]
      : []),
    {
      label: "Fill out your onboarding availability",
      completed: productPrompts?.advisorOnboardingAvailabilityFormCompleted,
      url: `/forms/complete/${PROD_ONBOARDING_AVAILABILITY_FORM_UUID}`,
      dismissible: true,
      dismissed:
        productPrompts?.advisorOnboardingAvailabilityFormCompleted ===
        "dismissed",
      onDismiss: () =>
        handleDismissTask("advisorOnboardingAvailabilityFormCompleted"),
    },
    {
      label: "Add partner as an account holder",
      completed: productPrompts?.hasAddedPartner,
      url: `/settings?addMember=true`,
      dismissible: true,
      dismissed: productPrompts?.hasAddedPartner === "dismissed",
      onDismiss: () => handleDismissTask("hasAddedPartner"),
    },
    ...(showGoogleCalendarSyncUpsell
      ? [
          {
            label: "Enable edit access for your Google calendar",
            completed: false,
            url: `/settings/calendars?from=onboarding`,
            dismissible: true,
            dismissed:
              productPrompts?.hasTwoWayGoogleCalendarSync === "dismissed",
            onDismiss: () => handleDismissTask("hasTwoWayGoogleCalendarSync"),
          },
        ]
      : []),
    ...additionalTasks,
  ];

  const digitalTasks: OnboardingTask[] = [
    ...(!showGoogleCalendarSyncUpsell
      ? [
          {
            label: "Sync your calendar",
            completed: productPrompts?.hasCalendarIntegration,
            url: "/settings/calendars?from=onboarding",
            dismissible: true,
            dismissed: productPrompts?.hasCalendarIntegration === "dismissed",
            onDismiss: () => handleDismissTask("hasCalendarIntegration"),
          },
        ]
      : []),
    {
      label: "Share household details",
      completed: productPrompts?.hasCompletedHouseholdInfo,
      url: "/forms/complete/c2947631-591e-458d-bc64-5952356e82a7",
      dismissible: true,
      dismissed: productPrompts?.hasCompletedHouseholdInfo === "dismissed",
      onDismiss: () => handleDismissTask("hasCompletedHouseholdInfo"),
    },
    {
      label: "Add a partner",
      completed: productPrompts?.hasAddedPartner,
      url: `/settings?addMember=true`,
      dismissible: true,
      dismissed: productPrompts?.hasAddedPartner === "dismissed",
      onDismiss: () => handleDismissTask("hasAddedPartner"),
    },
    {
      label: "Reply to your agent's text",
      completed: productPrompts?.hasTextedAgent,
      tooltip:
        "Your agent should have just texted you. Reply to ask a question, delegate a task, or just say hi.",
      dismissible: true,
      dismissed: productPrompts?.hasTextedAgent === "dismissed",
      onDismiss: () => handleDismissTask("hasTextedAgent"),
    },
    {
      label: "Create your first routine",
      completed: productPrompts?.hasCreatedSmartReminder,
      dismissible: true,
      dismissed: productPrompts?.hasCreatedSmartReminder === "dismissed",
      onDismiss: () => handleDismissTask("hasCreatedSmartReminder"),
      onClick: () =>
        navigate("/conversations/in-app", {
          state: {
            prefill:
              "On thursday evening check the weather, my calendar and what's happening in my area and suggest some things I might want to consider doing?",
          },
        }),
    },
    ...(showGoogleCalendarSyncUpsell
      ? [
          {
            label: "Enable edit access for your Google calendar",
            completed: false,
            url: `/settings/calendars?from=onboarding`,
            dismissible: true,
            dismissed:
              productPrompts?.hasTwoWayGoogleCalendarSync === "dismissed",
            onDismiss: () => handleDismissTask("hasTwoWayGoogleCalendarSync"),
          },
        ]
      : []),
    ...additionalTasks,
  ];

  const tasks = isDigitalSubscription ? digitalTasks : standardTasks;

  if (!productPrompts || tasks.every((t) => t.dismissed || t.completed)) {
    return null;
  }
  return (
    <Collapse in={!!productPrompts} timeout={800}>
      <OnboardingModule
        tasks={tasks}
        title="Welcome to Faye!"
        subtitle={memberOnboardingWelcomeSubtitle({
          isDigitalSubscription,
          isUnpaidSignup,
          familyStatus,
        })}
        dismissKey="member-onboarding-dismissed"
        dismissDays={7}
        collapseSx={{ marginBottom: "0px" }}
        showDismiss={false}
      />
    </Collapse>
  );
};
